/* eslint-disable no-restricted-syntax */
'use client';

import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDebouncedCallback } from '@/hooks/useDebouncedValue';

// Helper functions for color conversion
const hslToHex = (h: number, s: number, l: number) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const hexToHsl = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 0, 0];

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

const _normalizeColor = (color: string): string => {
  if (color.startsWith('#')) {
    return color.toUpperCase();
  } else if (color.startsWith('hsl')) {
    const [h, s, l] = color.match(/\d+(\.\d+)?/g)?.map(Number) || [0, 0, 0];
    return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
  }
  return color;
};

const trimColorString = (color: string, maxLength: number = 20): string => {
  if (color.length <= maxLength) return color;
  return `${color.slice(0, maxLength - 3)}...`;
};

// Convert hex to HSL string format expected by the original component
const _hexToHslString = (hex: string): string => {
  const [h, s, l] = hexToHsl(hex);
  return `hsl(${h}, ${s}%, ${l}%)`;
};

// Convert HSL string to hex
const _hslStringToHex = (hslString: string): string => {
  const [h, s, l] = hslString.match(/\d+(\.\d+)?/g)?.map(Number) || [0, 0, 0];
  return hslToHex(h, s, l);
};

export function ColorPicker({
  color,
  onChange,
  responsive = false,
}: {
  color: string;
  onChange: (color: string) => void;
  responsive?: boolean;
}) {
  const isMobile = useIsMobile();
  const [hsl, setHsl] = useState<[number, number, number]>([0, 0, 0]);
  const [colorInput, setColorInput] = useState(color);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (color) {
      let displayColor = color;
      if (color.startsWith('#')) {
        displayColor = color;
        const [h, s, l] = hexToHsl(color);
        setHsl([h, s, l]);
        setColorInput(color);
      } else if (color.startsWith('hsl')) {
        const [h, s, l] = color.match(/\d+(\.\d+)?/g)?.map(Number) || [0, 0, 0];
        setHsl([h, s, l]);
        displayColor = hslToHex(h, s, l);
        setColorInput(displayColor);
      }
    }
  }, [color]);

  // `onChange` typically wires straight to a network write (e.g. brand kit
  // settings persist on every call — see IntegratedBrandKit.handleColorChange).
  // Local hsl/colorInput state still updates on every pixel of drag or
  // keystroke for instant visual feedback; only the outward `onChange` commit
  // is debounced, so a drag or hue-slide fires one write per gesture instead
  // of one per pixel. Preset swatches are a single deliberate click and skip
  // the debounce for instant feedback.
  const debouncedOnChange = useDebouncedCallback(onChange, 300);

  const handleColorChange = (newColor: string, immediate = false) => {
    let h, s, l;
    if (newColor.startsWith('#')) {
      [h, s, l] = hexToHsl(newColor);
      setColorInput(newColor);
    } else if (newColor.startsWith('hsl')) {
      [h, s, l] = newColor.match(/\d+(\.\d+)?/g)?.map(Number) || [0, 0, 0];
      setColorInput(hslToHex(h, s, l));
    }

    setHsl([h, s, l]);
    // Always return hex format to maintain consistency with the original component
    const hexColor = hslToHex(h, s, l);
    if (immediate) onChange(hexColor);
    else debouncedOnChange(hexColor);
  };

  const handleHueChange = (hue: number) => {
    const newHsl: [number, number, number] = [hue, hsl[1], hsl[2]];
    setHsl(newHsl);
    const hexColor = hslToHex(newHsl[0], newHsl[1], newHsl[2]);
    setColorInput(hexColor);
    debouncedOnChange(hexColor);
  };

  const handleSaturationLightnessChange = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const s = Math.round((x / rect.width) * 100);
    const l = Math.round(100 - (y / rect.height) * 100);
    const newHsl: [number, number, number] = [hsl[0], s, l];
    setHsl(newHsl);
    const hexColor = hslToHex(newHsl[0], newHsl[1], newHsl[2]);
    setColorInput(hexColor);
    debouncedOnChange(hexColor);
  };

  const handleColorInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;
    setColorInput(newColor);
    if (/^#[0-9A-Fa-f]{6}$/.test(newColor)) {
      handleColorChange(newColor);
    }
  };

  const colorPresets = [
    '#FF3B30',
    '#FF9500',
    '#FFCC00',
    '#4CD964',
    '#5AC8FA',
    '#007AFF',
    '#5856D6',
    '#FF2D55',
    '#8E8E93',
    '#6B7280',
    '#374151',
    '#111827',
  ];

  const buttonContent = (
    <Button
      variant="outline"
      className="w-full justify-start text-left font-normal relative overflow-hidden"
    >
      <div
        className="w-4 h-4 rounded-full mr-2 shadow-sm border border-border/50"
        style={{ backgroundColor: colorInput }}
      />
      <span className="flex-grow">{trimColorString(colorInput)}</span>
      <CaretDownIcon className="h-4 w-4 opacity-50" />
      {responsive && isMobile && (
        <input
          type="color"
          value={colorInput}
          onChange={(e) => handleColorChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      )}
    </Button>
  );

  if (responsive && isMobile) {
    return buttonContent;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{buttonContent}</PopoverTrigger>
      <PopoverContent className="w-[240px] p-3">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="space-y-3"
        >
          <motion.div
            className="w-full h-40 rounded-lg cursor-crosshair relative overflow-hidden"
            style={{
              background: `
                linear-gradient(to top, rgba(0, 0, 0, 1), transparent),
                linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 0, 0, 0)),
                hsl(${hsl[0]}, 100%, 50%)
              `,
            }}
            onClick={handleSaturationLightnessChange}
          >
            <motion.div
              className="w-4 h-4 rounded-full border-2 border-white absolute shadow-md"
              style={{
                left: `${Math.max(0, Math.min(100, hsl[1]))}%`,
                top: `${Math.max(0, Math.min(100, 100 - hsl[2]))}%`,
                backgroundColor: `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`,
                transform: 'translate(-50%, -50%)',
              }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          </motion.div>
          <motion.input
            type="range"
            min="0"
            max="360"
            value={hsl[0]}
            onChange={(e) => handleHueChange(Number(e.target.value))}
            className="w-full h-3 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, 
                hsl(0, 100%, 50%), hsl(60, 100%, 50%), hsl(120, 100%, 50%), 
                hsl(180, 100%, 50%), hsl(240, 100%, 50%), hsl(300, 100%, 50%), hsl(360, 100%, 50%)
              )`,
            }}
          />
          <div className="flex items-center space-x-2">
            <Label htmlFor="color-input" className="sr-only">
              Color
            </Label>
            <Input
              id="color-input"
              type="text"
              value={colorInput}
              onChange={handleColorInputChange}
              className="flex-grow text-sm h-8 px-2"
              placeholder="#RRGGBB"
            />
            <motion.div
              className="w-8 h-8 rounded-md shadow-sm border border-border/50"
              style={{ backgroundColor: colorInput }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            />
          </div>
          <div className="grid grid-cols-6 gap-2">
            <AnimatePresence>
              {colorPresets.map((preset) => (
                <motion.button
                  key={preset}
                  className="w-8 h-8 rounded-full relative border border-border/30"
                  style={{ backgroundColor: preset }}
                  onClick={() => handleColorChange(preset, true)}
                  whileHover={{ scale: 1.2, zIndex: 1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {colorInput === preset && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CheckIcon
                        className="w-4 h-4 text-white absolute inset-0 m-auto"
                        weight="bold"
                      />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </PopoverContent>
    </Popover>
  );
}
